/**
 * Exam distribution (brief §teacher: `/teacher/exams`): score histogram with
 * G/VG threshold lines, verdict counts, per-chapter self-score ratio and the
 * 50 most recent sessions across the whole class.
 */
import {
  Badge,
  Card,
  EmptyState,
  PageHeader,
  Spinner,
  StatTile,
  Table,
} from "@client/components/ui";
import { paperLabel } from "@client/features/exam/paper";
import { useLang, useT } from "@client/i18n";
import { formatDate, formatNumber, formatPercent, formatPoints } from "@client/lib/format";
import { verdictTone } from "@client/lib/verdict";
import { ExamHistogramChart } from "./ExamHistogramChart";
import { TeacherNav } from "./TeacherNav";
import { useTeacherExams } from "./api";
import type { ExamRecentSession } from "./types";

export function TeacherExamsPage() {
  const t = useT();
  const { lang } = useLang();
  const query = useTeacherExams();

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title={t("teacher.exams.title")} description={t("teacher.exams.description")} />
      <TeacherNav />

      {query.isLoading && <Spinner size="md" label={t("common.loading")} />}
      {!query.isLoading && !query.data && <EmptyState title={t("common.error")} />}

      {query.data && (
        <>
          <div className="mb-8 grid grid-cols-3 gap-3">
            <StatTile
              label={t("teacher.exams.stats.vg")}
              value={formatNumber(query.data.verdicts.VG, lang)}
              tone="success"
            />
            <StatTile
              label={t("teacher.exams.stats.g")}
              value={formatNumber(query.data.verdicts.G, lang)}
              tone="accent"
            />
            <StatTile
              label={t("teacher.exams.stats.u")}
              value={formatNumber(query.data.verdicts.U, lang)}
              tone="warning"
            />
          </div>

          <section className="mb-8">
            <Card title={t("teacher.exams.chart.title")} compact>
              <ExamHistogramChart histogram={query.data.histogram} />
            </Card>
          </section>

          <section className="mb-8">
            <h2 className="mb-3 font-display text-lg text-ink-900 dark:text-ink-50">
              {t("teacher.exams.byChapter.title")}
            </h2>
            <div className="space-y-2">
              {query.data.byChapter.map((c) => (
                <div key={c.chapter} className="flex items-center gap-3">
                  <span className="w-20 shrink-0 text-sm text-ink-600 dark:text-ink-400">
                    {t("teacher.chapterLabel")} {c.chapter}
                  </span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-ink-100 dark:bg-ink-800">
                    <div
                      className="h-full rounded-full bg-accent-600 dark:bg-accent-400"
                      style={{ width: `${Math.round(c.meanSelfScoreRatio * 100)}%` }}
                    />
                  </div>
                  <span className="w-14 shrink-0 text-right text-sm tabular-nums text-ink-700 dark:text-ink-200">
                    {formatPercent(c.meanSelfScoreRatio, lang)}
                  </span>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="mb-3 font-display text-lg text-ink-900 dark:text-ink-50">
              {t("teacher.exams.recent.title")}
            </h2>
            <Table<ExamRecentSession>
              columns={[
                { key: "studentName", header: t("teacher.exams.recent.student") },
                {
                  key: "kind",
                  header: t("teacher.exams.recent.kind"),
                  cell: (row) => paperLabel(row, t) ?? t("teacher.examKind.generated"),
                },
                {
                  key: "startedAt",
                  header: t("teacher.exams.recent.date"),
                  align: "right",
                  cell: (row) => formatDate(row.startedAt, lang),
                },
                {
                  key: "totalScore",
                  header: t("teacher.exams.recent.score"),
                  align: "right",
                  cell: (row) =>
                    row.totalScore === null ? "—" : formatPoints(row.totalScore, lang),
                },
                {
                  key: "verdict",
                  header: t("teacher.exams.recent.verdict"),
                  align: "right",
                  cell: (row) =>
                    row.verdict ? (
                      <Badge tone={verdictTone(row.verdict)}>{row.verdict}</Badge>
                    ) : (
                      <Badge>{t(`teacher.examStatus.${row.status}`)}</Badge>
                    ),
                },
              ]}
              rows={query.data.recent}
              rowKey={(row) => row.id}
              empty={
                <p className="text-sm text-ink-600 dark:text-ink-400">
                  {t("teacher.exams.recent.empty")}
                </p>
              }
            />
          </section>
        </>
      )}
    </div>
  );
}

export default TeacherExamsPage;
